import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { CIVIL_REGISTRY_FAQ_CATEGORIES } from "@/content/civil-registry-corpus/faq-defs";

export const Route = createFileRoute("/en/civil-registry/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/civil-registry-corpus");
    return { faqs: m.CIVIL_REGISTRY_FAQ_EN };
  },
  component: CivilRegistryFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Thai Civil Registration FAQ — Birth, Death, House Register (${faqs.length} Answers)`,
        description:
          "Birth and death registration deadlines, the Thai house register and yellow book, name changes, ID cards, marriage and divorce records, legitimation, births abroad and Thai nationality, plus translation and legalisation for overseas use.",
        path: "/en/civil-registry/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/civil-registry/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Civil Registration FAQ", url: `${site}/en/civil-registry/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/civil-registry/faq")) },
      ],
    };
  },
});

function CivilRegistryFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Civil Registration FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Thai Civil Registration &amp; Personal Status Documents — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground">
          Answers follow the Civil Registration Act B.E. 2534, Person Name Act B.E. 2505, ID Card Act B.E. 2526,
          Nationality Act B.E. 2508, the Civil and Commercial Code, the Bureau of Registration Administration
          (bora.dopa.go.th) and the Department of Consular Affairs (consular.mfa.go.th). This is general information;
          district-office practice varies, so confirm with the office before you file.
        </p>
        <section aria-label="Topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {CIVIL_REGISTRY_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/civil-registry/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <FaqSourceNote topic="civil-registry" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}